export default function Terms() {
  const sections = [
    {
      title: "Acceptance of Terms",
      content: [
        "By accessing or using ExamLock's services, you agree to be bound by these Terms of Service and our Privacy Policy.",
        "If you do not agree to these terms, you may not use our services.",
        "These terms apply to all users, including teachers, students, administrators, and visitors.",
        "We may update these terms periodically, and continued use constitutes acceptance of any changes."
      ]
    },
    {
      title: "Beta Status and Service Description",
      content: [
        "⚠️ BETA SERVICE: ExamLock is currently in beta testing. Features may change, and temporary service disruptions may occur as we improve the platform.",
        "ExamLock provides an AI-powered online examination platform for educational institutions and teachers.",
        "Our services include exam creation tools, real-time monitoring, cheating detection, and performance analytics.",
        "We offer a 14-day trial (10 students, 50 AI questions, 3 saved quizzes) and paid plans with unlimited usage.",
        "Services are provided 'as is' and we continuously work to improve functionality and reliability.",
        "During beta, we may reset or modify features based on user feedback."
      ]
    },
    {
      title: "User Accounts and Registration",
      content: [
        "Teachers must create accounts to use our services and are responsible for maintaining account security.",
        "You must provide accurate and complete information during registration.",
        "You are responsible for all activities that occur under your account.",
        "Student accounts are created and managed by teachers; students access exams through teacher-provided links.",
        "We reserve the right to suspend or terminate accounts that violate these terms."
      ]
    },
    {
      title: "Acceptable Use Policy",
      content: [
        "You may use ExamLock only for lawful educational purposes.",
        "Prohibited activities include:",
        "• Attempting to circumvent security measures or abuse detection systems",
        "• Sharing account credentials or accessing accounts without authorization",
        "• Manipulating usage counters (AI questions, saved quizzes, student limits) via client-side tampering",
        "• Using the service for commercial purposes without authorization",
        "• Uploading malicious content, spam, or inappropriate material",
        "• Interfering with other users' access or service availability",
        "• Violating student privacy or educational data protection laws (FERPA, COPPA)",
        "Teachers are responsible for ensuring their use complies with applicable educational privacy laws.",
        "Any attempt to reverse engineer, hack, or compromise our systems is strictly prohibited and may result in immediate termination and legal action."
      ]
    },
    {
      title: "Educational Content and Intellectual Property",
      content: [
        "Teachers retain ownership of the educational content they create using our platform.",
        "ExamLock retains ownership of our software, algorithms, and platform technology.",
        "Teachers grant us a limited license to host and deliver their content through our platform.",
        "We respect copyright and will respond to valid DMCA takedown requests.",
        "Users must not upload copyrighted content without proper authorization."
      ]
    },
    {
      title: "Student Data and Privacy",
      content: [
        "We are committed to protecting student privacy in accordance with FERPA, COPPA, and other applicable laws.",
        "Student data is used only for educational purposes and is not shared with third parties for commercial purposes.",
        "Teachers control student data and can request deletion at any time.",
        "We implement industry-standard security measures to protect all user data.",
        "Detailed information about our data practices is available in our Privacy Policy."
      ]
    },
    {
      title: "Subscription, Billing, and Plan Limits",
      content: [
        "Trial Plan: 14 days, 10 students, 50 AI questions (total for trial), 3 saved quizzes, unlimited quiz creation.",
        "Paid Plans: $9.99/month or $99.99/year, 50 students (default), unlimited AI questions, unlimited saved quizzes.",
        "Plan limits are enforced server-side. Client-side manipulation will not bypass restrictions.",
        "Paid subscriptions automatically renew unless cancelled before the renewal date.",
        "Subscription fees are charged in advance and are non-refundable except where required by law.",
        "We may change subscription prices with 30 days advance notice.",
        "Trial periods are limited to one per email/organization.",
        "Failure to pay subscription fees may result in service suspension or account downgrade to read-only mode."
      ]
    },
    {
      title: "Service Availability and Support",
      content: [
        "We strive to maintain 99.9% uptime but cannot guarantee uninterrupted service.",
        "Scheduled maintenance will be announced in advance when possible.",
        "Technical support is provided based on your subscription level.",
        "We are not liable for damages resulting from temporary service interruptions.",
        "Emergency support is available for critical issues affecting active examinations."
      ]
    },
    {
      title: "Limitation of Liability",
      content: [
        "ExamLock's liability is limited to the amount paid for the service in the 12 months preceding the claim.",
        "We are not liable for indirect, incidental, or consequential damages.",
        "We do not guarantee that the service will meet all specific educational requirements.",
        "Users are responsible for maintaining appropriate backups of their data.",
        "Some jurisdictions do not allow certain liability exclusions, so some limitations may not apply to you."
      ]
    },
    {
      title: "Indemnification",
      content: [
        "Users agree to indemnify ExamLock against claims arising from their use of the service.",
        "This includes claims related to content uploaded by users or violations of these terms.",
        "Educational institutions are responsible for ensuring their use complies with applicable laws and regulations.",
        "Users must notify us promptly of any potential claims or legal issues."
      ]
    },
    {
      title: "Termination",
      content: [
        "Either party may terminate the service agreement with appropriate notice.",
        "We may terminate accounts immediately for violations of these terms.",
        "Upon termination, users may export their data within 30 days, after which it will be deleted.",
        "Paid subscription fees are non-refundable upon termination except where required by law.",
        "Certain provisions of these terms survive termination, including intellectual property and liability limitations."
      ]
    },
    {
      title: "Dispute Resolution",
      content: [
        "Disputes will first be addressed through good faith negotiations.",
        "If unresolved, disputes may be submitted to binding arbitration.",
        "Arbitration will be conducted under the rules of the American Arbitration Association.",
        "Some jurisdictions may require court resolution of certain disputes.",
        "Class action lawsuits are waived except where prohibited by law."
      ]
    },
    {
      title: "Governing Law",
      content: [
        "These terms are governed by the laws of [State/Country] without regard to conflict of law principles.",
        "Any legal proceedings must be brought in the courts of [Jurisdiction].",
        "If any provision of these terms is found unenforceable, the remaining provisions continue in effect.",
        "These terms constitute the complete agreement between the parties regarding use of ExamLock's services."
      ]
    }
  ];

  const lastUpdated = "September 1, 2024";
  const effectiveDate = "September 1, 2024";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">📄 Terms of Service</h1>
            <p className="text-xl text-purple-100 mb-6 leading-relaxed">
              These terms govern your use of ExamLock's educational technology platform. 
              By using our service, you agree to these terms.
            </p>
            <div className="bg-white bg-opacity-20 rounded-lg px-6 py-4 inline-block">
              <div className="text-sm text-purple-100 mb-1">Last Updated: {lastUpdated}</div>
              <div className="font-bold">Effective Date: {effectiveDate}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Points Summary */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-blue-800 mb-6 flex items-center">
                <span className="mr-3">📋</span>
                Key Points Summary
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold text-blue-800 mb-4">Your Rights:</h3>
                  <ul className="space-y-2 text-blue-700">
                    <li>• Retain ownership of your educational content</li>
                    <li>• Export your data at any time</li>
                    <li>• Cancel your subscription with proper notice</li>
                    <li>• Receive technical support based on your plan</li>
                    <li>• Have your data protected under privacy laws</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-blue-800 mb-4">Your Responsibilities:</h3>
                  <ul className="space-y-2 text-blue-700">
                    <li>• Use the service for educational purposes only</li>
                    <li>• Maintain the security of your account</li>
                    <li>• Comply with applicable privacy laws</li>
                    <li>• Pay subscription fees on time</li>
                    <li>• Respect intellectual property rights</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Terms Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {sections.map((section, index) => (
              <div key={index} className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 border-b border-gray-200 pb-4">
                  {index + 1}. {section.title}
                </h2>
                
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="space-y-6">
                    {section.content.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-gray-700 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* Contact Information */}
            <div className="bg-purple-50 border border-purple-200 rounded-2xl p-8 mb-16">
              <h2 className="text-2xl font-bold text-purple-800 mb-6 flex items-center">
                <span className="mr-3">📞</span>
                Questions About These Terms?
              </h2>
              <div className="text-purple-700 space-y-4">
                <p>
                  If you have questions about these Terms of Service or need clarification 
                  on any provision, please contact our legal team:
                </p>
                <div className="bg-white rounded-lg p-4 space-y-2">
                  <div><strong>Email:</strong> legal@examlock.ai</div>
                  <div><strong>Address:</strong> ExamLock Legal Department, [Address]</div>
                  <div><strong>Phone:</strong> +1 (555) 123-EXAM</div>
                </div>
                <p className="text-sm">
                  For general support questions, please use our regular support channels 
                  available through your account dashboard.
                </p>
              </div>
            </div>

            {/* Version History Notice */}
            <div className="bg-gray-100 rounded-2xl p-8 mb-16">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-3">📝</span>
                Terms Update Policy
              </h3>
              <div className="text-gray-700 space-y-4">
                <p>
                  We may update these Terms of Service from time to time to reflect 
                  changes in our service, legal requirements, or business practices.
                </p>
                <div className="bg-white rounded-lg p-4">
                  <div className="space-y-2">
                    <div><strong>Notice Period:</strong> 30 days for material changes</div>
                    <div><strong>Notification Method:</strong> Email and in-app notifications</div>
                    <div><strong>Effective Date:</strong> Changes take effect after notice period</div>
                    <div><strong>Your Options:</strong> Continue using the service (acceptance) or terminate your account</div>
                  </div>
                </div>
                <p className="text-sm">
                  We recommend reviewing these terms periodically to stay informed of any updates.
                </p>
              </div>
            </div>

            {/* Educational Use Statement */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                <span className="mr-3">🎓</span>
                Educational Mission Statement
              </h3>
              <div className="text-green-700 space-y-4">
                <p>
                  ExamLock is designed specifically for educational use and is committed 
                  to supporting teachers and students in their educational journey.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold mb-2">Our Commitments:</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• Student privacy protection</li>
                      <li>• Educational law compliance</li>
                      <li>• Continuous platform improvement</li>
                      <li>• Responsive customer support</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2">Your Assurance:</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• Fair and transparent terms</li>
                      <li>• Predictable pricing</li>
                      <li>• Data portability</li>
                      <li>• Professional service standards</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}